import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import {
  podgladStanuAutomatu,
  przyjmijStanOdWlasciciela,
  wynikOdczytuRozjazdu,
  wyrownajStanAutomatu,
  zlecOdczytStanu,
} from '@/lib/machines-api';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { useToast } from '@/components/ui/toast';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ROZJAZD STANU MAGAZYNOWEGO JEDNEGO AUTOMATU — WIERSZ PO WIERSZU, Z PORTALEM JAKO STRONĄ.
 *
 *  ⚠️⚠️ ZMIERZONE 21.09.2026 NA ŚWIEŻEJ KOPII MASTERA: portal pokazywał dla jednego automatu
 *  80,8 %, master 76,5 % — przy TEJ SAMEJ liczbie pozycji. Czyli rozjazd siedział w ILOŚCIACH,
 *  a nie w planogramie. Sumy mówiły, że jest, i nie mówiły nic więcej: bez wierszy nie wiadomo,
 *  czy to jedna przegródka o sto sztuk, czy sto przegródek po jednej — a to dwie różne naprawy.
 *
 *  ⚠️⚠️ ODCZYT ROZJAZDU KONFIGURACJI TEGO NIE ODDA i nie powinien: świadomie wycina `QTYCURRENT`,
 *  bo między klonami różni się z definicji i dawałby różnicę w każdym wierszu. Tu prosimy o tę
 *  jedną kolumnę WPROST i porównujemy wyłącznie ją — dlatego to osobny ekran, a nie przełącznik.
 *
 *  ⚠️ PORTAL WYSTĘPUJE JAKO ZWYKŁA STRONA, pod nazwą, której nie da się pomylić z węzłem. Bez
 *  niego porównywalibyśmy węzły między sobą — czyli odpowiadali na inne pytanie niż zadane.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function RozjazdStanu({ groupId, vmid }: { groupId: string; vmid: number }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [otwarte, setOtwarte] = useState(false);
  const [zlecam, setZlecam] = useState(false);

  const dane = useQuery({
    queryKey: ['rozjazd-stanu', groupId, vmid],
    queryFn: () => wynikOdczytuRozjazdu(groupId, 'DrawerCompartmentItems', vmid),
    enabled: otwarte,
    /** ⚠️ Odpowiedź przychodzi po cyklu agenta, więc dopytujemy, dopóki któryś węzeł nie odpowiedział. */
    refetchInterval: (q) => (q.state.data?.stan === 'CZEKA' ? 5_000 : false),
  });

  const zapytaj = async (): Promise<void> => {
    setZlecam(true);
    setOtwarte(true);
    try {
      const w = await zlecOdczytStanu(groupId, vmid);
      toast.success(t('stanRoz.zlecono').replace('{n}', String(w.wezly.length)));
      /** ⚠️ Węzeł pominięty przez starszego agenta musi paść z nazwy — jego brak czyta się jak zgoda. */
      if ((w.zaStare?.length ?? 0) > 0) {
        toast.toast(t('stanRoz.zaStare').replace('{wezly}', (w.zaStare ?? []).join(', ')));
      }
      await qc.invalidateQueries({ queryKey: ['rozjazd-stanu', groupId, vmid] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZlecam(false);
    }
  };

  const o = dane.data ?? null;
  /** ⚠️ Odczyt konfiguracji pod tym samym kluczem nie jest odpowiedzią na TO pytanie. */
  const oStanie = o !== null && (o.kolumnyMaszyny?.length ?? 0) > 0;

  return (
    <div className="basis-full pl-2">
      <Button size="sm" variant="ghost" className="h-6 px-1 text-[11px]" disabled={zlecam} onClick={() => void zapytaj()}>
        {zlecam ? <Loader2 className="size-3 animate-spin" /> : <Search className="size-3" />}
        {t('stanRoz.pokaz')}
      </Button>

      {otwarte && o?.stan === 'CZEKA' && <p className="text-[11px] text-muted-foreground">{t('stanRoz.czekam')}</p>}

      {otwarte && o?.stan === 'BRAK' && <p className="text-[11px] text-muted-foreground">{t('stanRoz.nikt')}</p>}

      {otwarte && o?.stan === 'GOTOWE' && oStanie && (
        <div className="space-y-1 rounded border border-border bg-muted/20 p-2 text-[11px]">
          {/*
            ⚠️⚠️ „NIE WIEM" NIE MOŻE WYGLĄDAĆ JAK ZERO — i złamałem tę zasadę we własnym, nowym
            komponencie (21.09.2026). Węzeł, od którego wiersze nie dotarły, oddaje `null`,
            a `?? 0` zamieniał to w „ten węzeł nie ma ani jednego wiersza". Na ekranie wyszło
            „455 pozycji, których gdzieś nie ma" — alarm w całości zmyślony, a pod nim przycisk
            naprawy.
          */}
          <p className="text-muted-foreground">
            {o.wezly
              .map((w) => `${w.nodeId}: ${w.wierszy === null ? t('stanRoz.bezOdpowiedzi') : w.wierszy}`)
              .join(' · ')}
          </p>

          {o.rozne.length === 0 && o.brakujace.length === 0 && (
            <p className="text-[var(--success)]">{t('stanRoz.zgodne').replace('{n}', String(o.kluczyRazem))}</p>
          )}

          {o.rozne.length > 0 && (
            <p className="font-medium text-[var(--warning)]">
              {t('stanRoz.roznych').replace('{n}', String(o.rozne.length))}
            </p>
          )}

          {/*
            ⚠️⚠️ WARTOŚCI WSZYSTKICH STRON PRZY KAŻDYM KLUCZU, z portalem włącznie. Sam fakt „ta
            pozycja się różni" nie mówi, KTO jest do tyłu — a to jest jedyne, co rozstrzyga, w którą
            stronę naprawiać.
          */}
          {o.rozne.slice(0, 60).map((x) => (
            <p key={`r-${x.klucz}`} className="break-all">
              <span className="font-mono text-[var(--warning)]">{x.klucz}</span>{' '}
              {x.kolumny.map((kol) => (
                <span key={kol.kolumna} className="font-mono">
                  {kol.kolumna}:{' '}
                  {kol.grupy.map((g) => `${g.wartosc ?? '∅'} (${g.wezly.join(', ')})`).join('  vs  ')}{' '}
                </span>
              ))}
            </p>
          ))}

          {o.brakujace.length > 0 && (
            <p className="font-medium text-[var(--destructive)]">
              {t('stanRoz.brakujace').replace('{n}', String(o.brakujace.length))}
            </p>
          )}

          {o.brakujace.slice(0, 40).map((b) => (
            <p key={`b-${b.klucz}`} className="break-all">
              <span className="font-mono text-[var(--destructive)]">{b.klucz}</span>{' '}
              <span className="text-muted-foreground">nie ma: {b.nieMaja.join(', ')}</span>
            </p>
          ))}

          {/* ⚠️ Naprawa POD rozjazdem — kolejność na ekranie wymusza kolejność myślenia. */}
          {(o.rozne.length > 0 || o.brakujace.length > 0) && <Naprawa groupId={groupId} vmid={vmid} />}
        </div>
      )}
    </div>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  NAPRAWA ROZJAZDU — DWA KROKI I ZDANIE PRZED KAŻDYM Z NICH.
 *
 *  ⚠️⚠️ WYMAGANIE UŻYTKOWNIKA Z 22.09.2026: „musi być wyraźnie napisane co się zrobi". Wcześniej
 *  stał tu jeden przycisk „Roześlij stan ponownie", który nie mówił ani ile pozycji pojedzie, ani
 *  do ilu węzłów, ani — co najważniejsze — CZYJĄ wartość rozniesie.
 *
 *  ⚠️⚠️ KOLEJNOŚĆ KROKÓW JEST CAŁĄ TREŚCIĄ TEGO PANELU. Zmierzone na IKEA 21.09.2026: właściciel
 *  83 %, portal 79,4 %, master 75,6 %. Rozsyłka stanu portalu roznosiłaby wtedy liczbę o krok za
 *  późną na jedenaście baz. Najpierw portal uczy się od właściciela, dopiero potem rozsyła.
 *
 *  ⚠️ ZDANIA POCHODZĄ Z PORTALU, NIE Z `i18n` — tylko on wie, ile pozycji się różni, ile jest
 *  zamrożonych i ilu węzłów dotyczy rozsyłka. Zdanie sklejane w kliencie rozjechałoby się z tym,
 *  co przycisk naprawdę robi. Tą samą drogą idą tu wszystkie powody odmowy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function Naprawa({ groupId, vmid }: { groupId: string; vmid: number }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [pyta, setPyta] = useState<'przyjmij' | 'wyrownaj' | null>(null);
  const [pracuje, setPracuje] = useState(false);

  const podglad = useQuery({
    queryKey: ['podglad-stanu', groupId, vmid],
    queryFn: () => podgladStanuAutomatu(groupId, vmid),
  });

  const odswiez = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['podglad-stanu', groupId, vmid] }),
      qc.invalidateQueries({ queryKey: ['zajetosc', groupId] }),
    ]);
  };

  const zrob = async (co: 'przyjmij' | 'wyrownaj'): Promise<void> => {
    setPracuje(true);
    try {
      if (co === 'przyjmij') {
        const w = await przyjmijStanOdWlasciciela(groupId, vmid);
        toast.success(t('stanRoz.przyjeto').replace('{n}', String(w.zmienionych)));
      } else {
        const w = await wyrownajStanAutomatu(groupId, vmid);
        if (w.pozycji > 0) toast.success(t('stanRoz.wyrownano').replace('{n}', String(w.pozycji)));
        else toast.toast(t('stanRoz.nicDoRozeslania'));
      }
      setPyta(null);
      await odswiez();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setPracuje(false);
    }
  };

  const p = podglad.data ?? null;
  if (p === null) return null;

  return (
    <div className="space-y-2 border-t border-border pt-2">
      {/*
        ⚠️⚠️ KROK 1 STOI WYŻEJ I MA ŁAGODNIEJSZY PRZYCISK, bo nic nie wysyła: zmienia wyłącznie
        liczbę u nas. Krok 2 jest czerwony, bo kolejkuje zapisy do baz klienta.
      */}
      <Krok
        numer={1}
        etykieta={t('stanRoz.przyjmij')}
        opis={p.przyjecie.powod ?? p.przyjecie.opis}
        zastrzezenia={p.przyjecie.zastrzezenia}
        /** ⚠️ Powód odmowy zostaje na ekranie, ale przycisk gaśnie — inaczej klika się w ścianę. */
        aktywny={p.przyjecie.mozliwe}
        wariant="outline"
        pyta={pyta === 'przyjmij'}
        pracuje={pracuje}
        onPytaj={() => setPyta('przyjmij')}
        onAnuluj={() => setPyta(null)}
        onZrob={() => void zrob('przyjmij')}
      />

      <Krok
        numer={2}
        etykieta={t('stanRoz.wyrownaj')}
        opis={p.rozeslanie.opis}
        zastrzezenia={[]}
        aktywny={p.rozeslanie.pozycji > 0}
        wariant="destructive"
        pyta={pyta === 'wyrownaj'}
        pracuje={pracuje}
        onPytaj={() => setPyta('wyrownaj')}
        onAnuluj={() => setPyta(null)}
        onZrob={() => void zrob('wyrownaj')}
      />
    </div>
  );
}

/** Jeden krok naprawy: co się stanie, czego świadomie nie robi, i dopiero potem przycisk. */
function Krok({
  numer,
  etykieta,
  opis,
  zastrzezenia,
  aktywny,
  wariant,
  pyta,
  pracuje,
  onPytaj,
  onAnuluj,
  onZrob,
}: {
  numer: number;
  etykieta: string;
  opis: string;
  zastrzezenia: string[];
  aktywny: boolean;
  wariant: 'outline' | 'destructive';
  pyta: boolean;
  pracuje: boolean;
  onPytaj: () => void;
  onAnuluj: () => void;
  onZrob: () => void;
}) {
  const { t } = useI18n();

  return (
    <div className="space-y-1">
      <p className="font-medium">
        <span className="text-muted-foreground">{numer}.</span> {etykieta}
      </p>
      {/* ⚠️ Zdanie NAD przyciskiem, nie w podpowiedzi — ma być przeczytane, a nie do odkrycia. */}
      <p className="text-muted-foreground">{opis}</p>
      {zastrzezenia.map((z) => (
        <p key={z} className="text-[var(--warning)]">
          {z}
        </p>
      ))}

      {pyta ? (
        <div className="flex items-center gap-2">
          <Button size="sm" variant={wariant} disabled={pracuje} onClick={onZrob}>
            {pracuje ? <Loader2 className="size-4 animate-spin" /> : null}
            {t('stanRoz.potwierdz')}
          </Button>
          <Button size="sm" variant="outline" disabled={pracuje} onClick={onAnuluj}>
            {t('pelnyOdczyt.anuluj')}
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 text-[11px]"
          disabled={!aktywny || pracuje}
          onClick={onPytaj}
        >
          {etykieta}
        </Button>
      )}
    </div>
  );
}
